/**
 * Client for the backend-mediated (BFF) auth endpoints on the addin-api
 * (V-08 follow-up). The browser never sees the refresh token: it travels in an
 * encrypted HttpOnly cookie the backend sets, which is why every call here is
 * made with `credentials: "include"`. What does reach JS is short-lived only —
 * access/id/graph tokens (~15 min, held in memory / sessionStorage by
 * AuthStateProvider) plus the CSRF secret paired with the cookie.
 */

import { config } from "../../shared/config/environment";

/** Token bundle returned by /api/auth/token and /api/auth/refresh. */
export interface BffTokens {
  /** Bearer for the AIVA backend (API audience). */
  accessToken: string;
  /** OIDC id token — bearer for the .NET add-in API. */
  idToken: string | null;
  /** Graph-audience token. Entra only; null for other providers. */
  graphToken: string | null;
  /** Seconds until the shortest-lived token expires. */
  expiresIn: number;
  /** Double-submit CSRF secret paired with the session cookie. */
  csrfToken: string;
}

/** Error carrying the HTTP status so callers can tell a dead session (401) from a transient failure. */
export class BffAuthError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "BffAuthError";
    this.status = status;
  }
}

/** Wire envelope shared by every addin-api endpoint. */
interface BffWireResponse {
  success?: boolean;
  data?: {
    accessToken?: string;
    idToken?: string | null;
    graphToken?: string | null;
    expiresIn?: number;
    csrfToken?: string;
  } | null;
  error?: string | null;
}

/**
 * The CSRF secret is kept in localStorage rather than memory so a reopened
 * taskpane can silently restore its session from the cookie (the BFF
 * equivalent of the old `ssoSilent` mount path). Unlike the refresh token this
 * is safe to keep JS-readable: double-submit CSRF only defends against OTHER
 * origins, which can never read this origin's storage — and the secret is
 * useless without the HttpOnly cookie it is paired with.
 */
const CSRF_TOKEN_KEY = "aiva_bff_csrf";

export function storeCsrfToken(token: string): void {
  try {
    localStorage.setItem(CSRF_TOKEN_KEY, token);
  } catch { /* storage unavailable */ }
}

export function loadCsrfToken(): string | null {
  try {
    return localStorage.getItem(CSRF_TOKEN_KEY);
  } catch { /* storage unavailable */ }
  return null;
}

export function clearCsrfToken(): void {
  try {
    localStorage.removeItem(CSRF_TOKEN_KEY);
  } catch { /* storage unavailable */ }
}

/**
 * On cutover to the BFF flow, purge the caches the replaced browser-side
 * clients left in localStorage — the dialog MSAL cache and the oidc-client-ts
 * user store, both of which hold the refresh token V-08 flags. Key names are
 * dynamic (they embed client/tenant ids), so this scans by shape, mirroring
 * `purgeLegacyLocalRefreshTokens` in services/auth/storage.ts. Safe no-op once
 * they are gone.
 */
export function purgeLegacyIdpCaches(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const isMsalCache =
        key.startsWith("msal.") ||
        key.includes("login.microsoftonline.com") ||
        key.includes("login.windows.net");
      const isOidcCache = key.startsWith("oidc.");
      if (isMsalCache || isOidcCache) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch { /* storage unavailable */ }
}

function authUrl(path: string): string {
  if (!config.addinApiBaseUrl) {
    throw new BffAuthError(0, "Addin API base URL is not configured.");
  }
  return `${config.addinApiBaseUrl}/api/auth/${path}`;
}

async function postAuth(
  path: string,
  body: unknown,
  headers: Record<string, string>,
  fetchImpl: typeof fetch,
): Promise<BffTokens> {
  let response: Response;
  try {
    response = await fetchImpl(authUrl(path), {
      method: "POST",
      // The session cookie is the credential — without `include` the browser
      // strips it from this cross-origin call and refresh always 401s.
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json", ...headers },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new BffAuthError(0, err instanceof Error ? err.message : "Auth service unreachable.");
  }

  if (!response.ok) {
    // Non-production responses carry the exception message in the envelope's
    // `error` field — surface it so a failed exchange is diagnosable from the
    // dialog instead of a bare status code.
    let detail: string | null = null;
    try {
      detail = ((await response.json()) as BffWireResponse).error ?? null;
    } catch { /* body not JSON */ }
    throw new BffAuthError(response.status, detail ?? `Auth request failed with HTTP ${response.status}.`);
  }

  let payload: BffWireResponse;
  try {
    payload = (await response.json()) as BffWireResponse;
  } catch {
    throw new BffAuthError(response.status, "Auth response was not valid JSON.");
  }

  const data = payload.data;
  if (!payload.success || !data?.accessToken || !data.csrfToken) {
    throw new BffAuthError(response.status, payload.error ?? "Auth response was incomplete.");
  }

  return {
    accessToken: data.accessToken,
    idToken: data.idToken ?? null,
    graphToken: data.graphToken ?? null,
    expiresIn: data.expiresIn ?? 0,
    csrfToken: data.csrfToken,
  };
}

/**
 * Bearer header for the two post-sign-in routes. The gateway validates the
 * access token on `/refresh` and `/logout` exactly as it does on every other
 * route, so a call without it is rejected at the edge and never reaches the
 * backend. That is also why the taskpane must refresh *ahead* of expiry: the
 * token it presents here has to still be live for the call to get through.
 * `/token` sends none — it is the sign-in call, so no token exists yet.
 */
function bearer(accessToken: string | null): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/** One-time sign-in: posts the dialog's authorization code + PKCE verifier. */
export function exchangeAuthCode(
  params: { channel: string; code: string; codeVerifier: string; redirectUri: string },
  fetchImpl: typeof fetch = fetch,
): Promise<BffTokens> {
  return postAuth("token", params, {}, fetchImpl);
}

/**
 * Silent refresh: the still-valid access token gets the call through the
 * gateway, the cookie carries the refresh token, and the CSRF secret proves the
 * call originated from this app. Throws BffAuthError(401) when the session is
 * dead — including when the access token lapsed before this fired, which the
 * caller must treat as "session over", not as something to retry.
 */
export function refreshBffTokens(
  channel: string,
  csrfToken: string,
  accessToken: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<BffTokens> {
  return postAuth(
    "refresh",
    { channel },
    { "X-CSRF-Token": csrfToken, ...bearer(accessToken) },
    fetchImpl,
  );
}

/**
 * Clears the session cookie server-side. Best-effort — never throws, and the
 * caller signs out locally regardless of the outcome. A lapsed access token
 * means the gateway rejects this before the backend sees it; the cookie then
 * dies with the WebView instead of being cleared server-side, which is the
 * accepted trade for gating this route like every other one.
 */
export async function bffLogout(
  csrfToken: string | null,
  accessToken: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    await fetchImpl(authUrl("logout"), {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        ...bearer(accessToken),
      },
    });
  } catch { /* the local sign-out proceeds regardless */ }
}
